// Advanced analytics and learning metrics

const ANALYTICS_KEY = "cdl_analytics";
const GOALS_KEY = "cdl_goals";
const STREAK_KEY = "cdl_streak";

// Initialize analytics data structure
export function initializeAnalytics() {
  const existing = loadAnalytics();
  if (!existing || !existing.questionAttempts) {
    const analytics = {
      questionAttempts: {}, // { questionId: { attempts: [...], correctCount, totalAttempts, lastAttempt, avgTime } }
      sessionMetrics: [],   // Track session performance over time
      topicScores: {},      // { section: { topicName: { avg, count } } }
      studyStreaks: 0,
      lastStudyDate: null,
    };
    saveAnalytics(analytics);
    return analytics;
  }
  return existing;
}

export function loadAnalytics() {
  try {
    const raw = localStorage.getItem(ANALYTICS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function saveAnalytics(data) {
  try {
    localStorage.setItem(ANALYTICS_KEY, JSON.stringify(data));
  } catch {
    console.error('Failed to save analytics');
  }
}

// Track individual question attempt
export function recordQuestionAttempt(questionId, correct, timeSpent, section) {
  const analytics = initializeAnalytics();

  if (!analytics.questionAttempts[questionId]) {
    analytics.questionAttempts[questionId] = {
      attempts: [],
      correctCount: 0,
      totalAttempts: 0,
      lastAttempt: null,
      avgTime: 0,
      section,
    };
  }

  const q = analytics.questionAttempts[questionId];
  q.attempts.push({
    correct,
    timestamp: new Date().toISOString(),
    timeSpent,
  });

  q.totalAttempts += 1;
  if (correct) q.correctCount += 1;
  q.lastAttempt = new Date().toISOString();

  // Calculate average time
  const totalTime = q.attempts.reduce((sum, a) => sum + (a.timeSpent || 0), 0);
  q.avgTime = Math.round(totalTime / q.totalAttempts);

  saveAnalytics(analytics);
  return q;
}

// Get question mastery status
export function getQuestionMastery(questionId) {
  const analytics = loadAnalytics();
  if (!analytics || !analytics.questionAttempts[questionId]) {
    return { status: 'new', attempts: 0, correct: 0 };
  }

  const q = analytics.questionAttempts[questionId];
  const correctRate = q.totalAttempts > 0 ? q.correctCount / q.totalAttempts : 0;

  let status = 'learning';
  if (q.totalAttempts === 0) status = 'new';
  else if (correctRate >= 0.8 && q.totalAttempts >= 3) status = 'mastered';
  else if (correctRate >= 0.6) status = 'improving';
  else if (correctRate < 0.4) status = 'struggling';

  return {
    status,
    attempts: q.totalAttempts,
    correct: q.correctCount,
    correctRate: Math.round(correctRate * 100),
    lastAttempt: q.lastAttempt,
    avgTime: q.avgTime,
  };
}

// Get performance metrics for a section
export function getSectionMetrics(section) {
  const analytics = loadAnalytics();
  if (!analytics) return null;

  const sectionQuestions = Object.entries(analytics.questionAttempts)
    .filter(([, data]) => data.section === section)
    .map(([qId, data]) => ({
      questionId: qId,
      ...data,
    }));

  if (sectionQuestions.length === 0) {
    return { section, avgScore: 0, totalAttempts: 0, masteredCount: 0 };
  }

  const totalCorrect = sectionQuestions.reduce((sum, q) => sum + q.correctCount, 0);
  const totalAttempts = sectionQuestions.reduce((sum, q) => sum + q.totalAttempts, 0);
  const avgScore = totalAttempts > 0 ? Math.round((totalCorrect / totalAttempts) * 100) : 0;
  const masteredCount = sectionQuestions.filter(q => {
    const rate = q.totalAttempts > 0 ? q.correctCount / q.totalAttempts : 0;
    return rate >= 0.8 && q.totalAttempts >= 3;
  }).length;

  return {
    section,
    avgScore,
    totalAttempts,
    masteredCount,
    totalQuestions: sectionQuestions.length,
    strugglingQuestions: sectionQuestions.filter(q => {
      const rate = q.totalAttempts > 0 ? q.correctCount / q.totalAttempts : 0;
      return rate < 0.4;
    }).length,
  };
}

// Get overall performance summary
export function getPerformanceSummary() {
  const analytics = loadAnalytics();
  if (!analytics) return null;

  const allAttempts = Object.values(analytics.questionAttempts);
  const totalCorrect = allAttempts.reduce((sum, q) => sum + q.correctCount, 0);
  const totalAttempts = allAttempts.reduce((sum, q) => sum + q.totalAttempts, 0);
  const overallScore = totalAttempts > 0 ? Math.round((totalCorrect / totalAttempts) * 100) : 0;
  const masteredCount = allAttempts.filter(q => {
    const rate = q.totalAttempts > 0 ? q.correctCount / q.totalAttempts : 0;
    return rate >= 0.8 && q.totalAttempts >= 3;
  }).length;

  return {
    overallScore,
    totalAttempts,
    totalCorrect,
    masteredQuestions: masteredCount,
    totalTrackedQuestions: allAttempts.length,
  };
}

// Study Goals
export function setStudyGoal(goal) {
  try {
    localStorage.setItem(GOALS_KEY, JSON.stringify({
      ...goal,
      createdAt: new Date().toISOString(),
    }));
  } catch {
    console.error('Failed to save goal');
  }
}

export function getStudyGoal() {
  try {
    const raw = localStorage.getItem(GOALS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Study Streak
export function updateStreak() {
  try {
    const today = new Date().toDateString();
    const streakData = JSON.parse(localStorage.getItem(STREAK_KEY) || '{"lastDate": null, "count": 0}');

    const lastDate = streakData.lastDate ? new Date(streakData.lastDate).toDateString() : null;

    if (lastDate === today) {
      // Already studied today
      return streakData.count;
    }

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    if (lastDate === yesterday.toDateString()) {
      // Continue streak
      streakData.count += 1;
    } else {
      // Start new streak
      streakData.count = 1;
    }

    streakData.lastDate = today;
    localStorage.setItem(STREAK_KEY, JSON.stringify(streakData));
    return streakData.count;
  } catch {
    return 0;
  }
}

export function getStreak() {
  try {
    const streakData = JSON.parse(localStorage.getItem(STREAK_KEY) || '{"count": 0}');
    return streakData.count;
  } catch {
    return 0;
  }
}

// Get common misconceptions (questions where >60% users get it wrong)
export function getCommonMisconceptions() {
  const analytics = loadAnalytics();
  if (!analytics) return [];

  return Object.entries(analytics.questionAttempts)
    .filter(([, data]) => {
      const correctRate = data.totalAttempts > 0 ? data.correctCount / data.totalAttempts : 0;
      return correctRate < 0.4 && data.totalAttempts >= 2;
    })
    .map(([qId, data]) => ({
      questionId: qId,
      correctRate: Math.round((data.correctCount / data.totalAttempts) * 100),
      attempts: data.totalAttempts,
      section: data.section,
    }))
    .sort((a, b) => a.correctRate - b.correctRate)
    .slice(0, 10);
}

// Predict exam readiness
export function predictExamReadiness(section) {
  const metrics = getSectionMetrics(section);
  if (!metrics) return { readiness: 'unknown', daysToReady: 0 };

  const score = metrics.avgScore;
  const passThreshold = 80;
  const gap = passThreshold - score;

  if (score >= passThreshold) {
    return { readiness: 'ready', daysToReady: 0, score };
  }

  // Rough estimate: ~1% improvement per study session
  const estimatedDays = Math.max(1, Math.ceil(gap / 1.5));

  return {
    readiness: gap <= 10 ? 'almost' : 'keep_studying',
    daysToReady: estimatedDays,
    score,
    gap,
  };
}

// Peer comparison data (mock)
export function getPeerComparison(section) {
  const metrics = getSectionMetrics(section);
  if (!metrics) return null;

  // Mock peer data - in real app this would come from backend
  const peerAverages = {
    'General Knowledge': 72,
    'Air Brakes': 68,
    'Passenger': 75,
    'School Bus': 65,
    'Combination Vehicles': 60,
    'Hazmat': 62,
    'Tanker Vehicles': 58,
    'Doubles & Triples': 56,
  };

  const yourScore = metrics.avgScore;
  const peerAvg = peerAverages[section] || 65;
  const percentile = yourScore >= peerAvg ?
    Math.min(95, Math.round(50 + ((yourScore - peerAvg) / 20) * 50)) :
    Math.max(5, Math.round(50 - ((peerAvg - yourScore) / 20) * 50));

  return {
    section,
    yourScore,
    peerAverage: peerAvg,
    percentile,
    aheadBy: yourScore - peerAvg,
  };
}
